'use client';

import { Suspense, useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { parseISO } from 'date-fns';
import { Evento } from '@/types';
import { LABELS } from '@/lib/labels';
import CalendarioList from './CalendarioList';

interface Props {
  initialEventi: Evento[];
}

function getOggiMezzanotte(): Date {
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  return oggi;
}

function CalendarioClientViewInner({ initialEventi }: Props) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const vista: 'futuri' | 'archivio' =
    searchParams.get('vista') === 'archivio' ? 'archivio' : 'futuri';

  // Stato unico: tutti gli eventi pubblicati (futuri + passati).
  // Il filtro futuri/passati avviene lato client in base alla data corrente.
  const [eventiTutti, setEventiTutti] = useState<Evento[]>(initialEventi);
  const [loading, setLoading] = useState(false);

  // Aggiorna al mount con dati freschi da events.json.
  // Il parametro v= contiene il timestamp di build per invalidare la cache CDN ad ogni deploy.
  useEffect(() => {
    setLoading(true);
    const buildTime = process.env.NEXT_PUBLIC_BUILD_TIME ?? '0';
    fetch(`/events.json?v=${buildTime}`)
      .then((r) => r.json())
      .then((data: Evento[]) => setEventiTutti(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Filtro client-side calcolato ad ogni render — la soglia è sempre la data corrente
  const oggi = getOggiMezzanotte();
  const eventiFuturi = eventiTutti.filter((e) => parseISO(e.data) >= oggi);
  const eventiArchivio = eventiTutti
    .filter((e) => parseISO(e.data) < oggi)
    .sort((a, b) => parseISO(b.data).getTime() - parseISO(a.data).getTime());

  const currentEventi = vista === 'archivio' ? eventiArchivio : eventiFuturi;
  const isLoading = loading && eventiTutti.length === 0;

  const heading =
    vista === 'archivio' ? LABELS.calendario.tabArchivio : LABELS.calendario.prossimi;

  return (
    <div>
      {/* Tab di selezione vista */}
      <div
        className="flex gap-3 mb-8"
        role="tablist"
        aria-label={LABELS.calendario.tabLabel}
      >
        <button
          role="tab"
          aria-selected={vista === 'futuri'}
          onClick={() => router.push('/calendario/')}
          className={`min-h-[44px] px-5 py-2 rounded-xl font-section text-sm uppercase tracking-wide font-bold transition-colors ${
            vista === 'futuri'
              ? 'bg-accent-orange text-white'
              : 'border border-chalk text-chalk-dim hover:border-accent-orange hover:text-accent-orange'
          }`}
        >
          {LABELS.calendario.tabFuturi}
        </button>
        <button
          role="tab"
          aria-selected={vista === 'archivio'}
          onClick={() => router.push('/calendario/?vista=archivio')}
          className={`min-h-[44px] px-5 py-2 rounded-xl font-section text-sm uppercase tracking-wide font-bold transition-colors ${
            vista === 'archivio'
              ? 'bg-accent-orange text-white'
              : 'border border-chalk text-chalk-dim hover:border-accent-orange hover:text-accent-orange'
          }`}
        >
          {LABELS.calendario.tabArchivio}
        </button>
      </div>

      {/* Heading sezione */}
      <h2 className="font-section text-2xl md:text-3xl text-chalk uppercase tracking-widest mb-8">
        {heading}
      </h2>

      {/* Lista eventi */}
      {isLoading ? (
        <div className="text-center py-20">
          <p className="font-chalk text-2xl text-chalk-dim">Caricamento...</p>
        </div>
      ) : (
        <CalendarioList eventi={currentEventi} vista={vista} />
      )}
    </div>
  );
}

export default function CalendarioClientView({ initialEventi }: Props) {
  return (
    <Suspense
      fallback={
        <div>
          <h2 className="font-section text-2xl md:text-3xl text-chalk uppercase tracking-widest mb-8">
            {LABELS.calendario.prossimi}
          </h2>
          <CalendarioList eventi={initialEventi} vista="futuri" />
        </div>
      }
    >
      <CalendarioClientViewInner initialEventi={initialEventi} />
    </Suspense>
  );
}
